import { Fragment } from 'react'

import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
} from '@/components/ui/dropdown-menu'

import { formatForDisplay } from '@tanstack/react-hotkeys'

import type { CommandGroup } from './command.types'
import {
  executeCommand,
  getVisibleCommandGroups,
  isCommandEnabled,
} from './command.utils'

/**
 * Renders command groups as menu items.
 *
 * @remarks
 *   Uses the `DropdownMenu*` wrappers, which sit on the same Base UI `Menu`
 *   parts that `ContextMenu` re-exports. So it works inside both a dropdown and
 *   a context menu. Do not swap them for `ContextMenu*` wrappers.
 */
export function CommandMenuItems<Context>({
  groups,
  context,
}: {
  groups: CommandGroup<Context>[]
  context: Context
}) {
  const visibleGroups = getVisibleCommandGroups(groups, context)

  return visibleGroups.map((group, index) => (
    <Fragment key={index}>
      {index > 0 && <DropdownMenuSeparator />}

      <DropdownMenuGroup>
        {group.commands.map((command) => {
          const shortcut = command.shortcuts?.[0]

          return (
            <DropdownMenuItem
              key={command.id}
              variant={command.variant}
              disabled={!isCommandEnabled(command, context)}
              onClick={() => executeCommand(command, context)}
            >
              {command.icon && <command.icon />}
              {command.label}

              {shortcut && (
                <DropdownMenuShortcut>
                  {formatForDisplay(shortcut, { separatorToken: '' })}
                </DropdownMenuShortcut>
              )}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuGroup>
    </Fragment>
  ))
}
